// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, AppFooter } from "./common";

// System Settings — 시스템 설정
// Platform role: AI matching weights, notification policies, security, integrations.

const SECTIONS = [
  { k: "general",     icon: "settings",  label: "일반"    },
  { k: "permissions", icon: "approve",   label: "권한"    },
  { k: "matching",    icon: "match",     label: "AI 매칭" },
  { k: "notification",icon: "bell",      label: "알림"    },
  { k: "security",    icon: "approve",   label: "보안"    },
  { k: "data",        icon: "chart",     label: "데이터·보존" },
  { k: "integration", icon: "org",       label: "연동"    },
];

// All sections persist via /api/v1/settings now — rows are seeded for every
// section (db/seeds/settings.rb) and the generic PATCH handles any section.
// 6개 차원 = 매칭 엔진(Matches::Regenerator)이 실제 채점하는 축. 백엔드
// Setting::MATCHING_WEIGHT_DEFAULTS · 프런트 matchDimensions.ts 와 동일.
const DEFAULT_WEIGHTS = { distance: 25, qualification: 20, availability: 15, load: 15, history: 15, rating: 10 };

// 매칭 외 섹션이 저장하는 키 목록. save()가 이 키들만 모아 PATCH 한다 —
// 화면의 정보성(읽기 전용) 행은 제외하고 편집 가능한 필드만 보낸다.
const SECTION_KEYS = {
  general:      ["site_name", "default_timezone", "default_locale", "support_phone", "emergency_line", "maintenance_mode"],
  notification: ["push_enabled", "sms_enabled", "email_enabled", "quiet_hours_enabled"],
  security:     ["session_timeout_hours", "password_min_length", "password_max_age_days", "lockout_max_attempts"],
  data:         ["retention_audit_short_days", "retention_audit_long_days", "retention_visit_record_years", "anonymized_stats_enabled", "deletion_sla_days"],
  integration:  ["nhis_enabled", "kakao_alimtalk_enabled", "slack_ops_enabled", "public_data_demographics_enabled", "nts_business_verify_enabled"],
};
// 매칭 정책(가중치 외) 키 — saveMatching이 가중치와 함께 보낸다.
const MATCHING_POLICY_KEYS = ["auto_assign_threshold", "min_candidates", "response_wait_min", "block_repeat_manager"];

// Role columns for the permission matrix. Order = least → most privileged.
// system_admin column is rendered but disabled (always-on superuser).
const PERMISSION_ROLES = [
  { k: "citizen",      label: "어르신" },
  { k: "guardian",     label: "보호자" },
  { k: "manager",      label: "케어매니저" },
  { k: "agency",       label: "유관기관" },
  { k: "region_admin", label: "권역 관리자" },
  { k: "system_admin", label: "시스템 관리자" },
];

// Capability labels for the matrix rows. The set/order must match
// Setting::PERMISSION_KEYS on the Rails side — those rows are seeded by
// the migration. Add a row here when you add a capability to the
// PERMISSION_DEFAULTS hash in app/models/setting.rb.
const CAPABILITY_LABELS: Record<string, { label: string; desc: string }> = {
  can_approve:              { label: "승인 관리",         desc: "가입·기관·정책 승인/반려" },
  can_decide_matches:       { label: "매칭 의사결정",     desc: "매니저 본인 매칭 수락/거절. 운영자는 모든 매칭" },
  can_manage_matches:       { label: "매칭 운영",         desc: "권역 매칭 인박스·후보 재생성·가중치 시뮬레이션" },
  can_resolve_alerts:       { label: "이상 알림 처리",    desc: "위험 알림 종결" },
  can_manage_cases:         { label: "케이스 관리",       desc: "케이스 생성·재배정·상태 변경" },
  can_manage_elderly_risk:  { label: "위험도 변경",       desc: "어르신 위험등급(위험/주의/안정) 조정 (매니저는 본인 담당만)" },
  can_write_visits:         { label: "방문 일정 작성",    desc: "방문 생성·수정" },
  can_manage_regions:       { label: "권역 관리",         desc: "권역 생성·수정·삭제·통계 재계산 (조회는 권한 무관·스코프 적용)" },
  can_update_org_availability: { label: "기관 가용성 갱신",  desc: "기관이 자기 급여의 수용 가능 여부를 직접 보고 (자원은행 · 기관 담당자에게 열린 유일한 쓰기 권한)" },
  can_respond_referrals:    { label: "서비스 의뢰 응답",  desc: "받은 의뢰에 연락·수용·거절로 답하기 (기관 담당자 · 권역 관리자는 전화 응답 대리기록)" },
  can_manage_organizations: { label: "유관기관 관리",     desc: "기관 생성·수정·삭제 (권역 관리자는 자기 권역만 · 병합·일괄·통계재집계는 시스템 관리자 전용)" },
  can_manage_managers:      { label: "케어매니저 관리",   desc: "매니저 생성·수정·삭제 (조회는 권한 무관·스코프 적용)" },
  can_transfer_managers:    { label: "케어매니저 소속 변경", desc: "매니저를 다른 기관으로 재배정 (권역 관리자는 자기 권역 안에서만 · 타권역 이동은 매니저 본인 신청)" },
  can_manage_admins:        { label: "관리자 계정 관리",  desc: "관리자 초대·잠금 해제" },
  can_manage_members:       { label: "회원 관리",         desc: "어르신·보호자 탈퇴·재활성화 (권역 관리자는 자기 권역만)" },
  can_manage_settings:      { label: "시스템 설정 변경",  desc: "이 페이지에서 정책 저장" },
  can_export_regions:       { label: "권역 CSV 내보내기",     desc: "권역 비교표 CSV 다운로드" },
  can_export_care_managers: { label: "케어매니저 CSV 내보내기", desc: "케어매니저 목록 CSV 다운로드" },
  can_export_orgs:          { label: "유관기관 CSV 내보내기",   desc: "기관 목록 CSV 다운로드 (권역 한정 역할은 자기 권역만)" },
  can_view_reports:         { label: "리포트 조회",            desc: "리포트 화면·발행 이력 열람" },
  can_create_reports:       { label: "리포트 생성",            desc: "임시 리포트 생성 / 백그라운드 발행 요청" },
};

// 시스템 관리자 전용으로 "고정"된 권한 — 백엔드가 require_capability!(매트릭스 연동)이
// 아니라 require_system_admin!로 하드코딩해 보호한다. 매트릭스에서 다른 역할에 켜도
// API는 여전히 403을 내므로, 오해를 막기 위해 행을 읽기전용(🔒)으로 잠근다.
// 다른 역할에 위임할 의도가 생기면 해당 컨트롤러를 require_capability!로 바꾼 뒤
// 여기서 키를 제거하면 된다.
const LOCKED_CAPABILITIES = new Set([
  "can_manage_regions",
  // can_manage_organizations: 컨트롤러를 require_capability!로 전환(생성/수정/삭제)했으므로
  // 잠금 해제 — 매트릭스에서 다른 역할(예: region_admin)에 위임 가능. 병합·일괄·통계재집계는
  // 컨트롤러에서 별도로 require_system_admin!로 남아 있어 위임 대상이 아니다.
  "can_manage_admins",
  "can_manage_settings",
]);

// 새로고침/딥링크 시에도 현재 탭을 유지하도록 URL 해시(#general 등)에 활성 섹션을
// 보존한다. 유효한 섹션 해시가 있으면 그것을, 없으면 "general"(일반)로 시작한다.
// (이전엔 무조건 "matching" 으로 초기화돼 새로고침마다 AI 매칭 탭으로 튀었다.)
const SECTION_KEYS_SET = new Set(SECTIONS.map(s => s.k));
const sectionFromHash = () => {
  const h = (typeof location !== "undefined" ? location.hash : "").replace(/^#/, "");
  return SECTION_KEYS_SET.has(h) ? h : "general";
};

function Settings() {
  const [section, setSectionState] = React.useState(sectionFromHash);
  // 탭 전환 시 상태 + URL 해시를 함께 갱신(히스토리 오염 방지 위해 replaceState).
  const setSection = React.useCallback((k) => {
    setSectionState(k);
    if (typeof history !== "undefined") history.replaceState(null, "", `#${k}`);
  }, []);
  // 뒤로/앞으로 가기 등 외부 해시 변경에도 탭 동기화.
  React.useEffect(() => {
    const onHash = () => setSectionState(sectionFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const [weights, setWeights] = React.useState(DEFAULT_WEIGHTS);
  const [allSettings, setAllSettings] = React.useState({});
  const [permissions, setPermissions] = React.useState<Record<string, string[]>>({});
  // form[section][key] = 현재 편집값. API의 cast value로 초기화.
  const [form, setForm] = React.useState<Record<string, Record<string, any>>>({});
  const [saved, setSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  // 저장 기준선 — 마지막으로 저장된(또는 로드된) 값. 현재 form과 비교해 '저장되지
  // 않은 변경'을 판정한다. 토글은 form(로컬)만 바꾸고 저장을 눌러야 반영되므로,
  // 변경이 즉시 적용된 것처럼 보이지 않도록 미저장 상태를 명시한다.
  const [baseline, setBaseline] = React.useState<Record<string, Record<string, any>>>({});
  const total = Object.values(weights).reduce((a, b) => a + b, 0);
  const resetWeights = () => setWeights({ ...DEFAULT_WEIGHTS });

  // 섹션/키 값 접근·수정 헬퍼. 값이 없으면 dflt.
  const fval = (sec, key, dflt = "") => {
    const v = form[sec]?.[key];
    return v === undefined || v === null ? dflt : v;
  };
  const fset = (sec, key) => (v) =>
    setForm(f => ({ ...f, [sec]: { ...(f[sec] || {}), [key]: v } }));

  React.useEffect(() => {
    fetch("/api/v1/settings").then(r => r.json()).then(d => {
      setAllSettings(d);
      const m = d.matching || {};
      setWeights({
        distance:      m.weight_distance?.value      ?? 25,
        qualification: m.weight_qualification?.value ?? 20,
        availability:  m.weight_availability?.value  ?? 15,
        load:          m.weight_load?.value          ?? 15,
        history:       m.weight_history?.value        ?? 15,
        rating:        m.weight_rating?.value         ?? 10,
      });
      // 전 섹션의 cast value를 form에 적재 (matching 정책 키 포함).
      const nextForm: Record<string, Record<string, any>> = {};
      Object.keys(d || {}).forEach(sec => {
        nextForm[sec] = {};
        Object.keys(d[sec] || {}).forEach(key => { nextForm[sec][key] = d[sec][key]?.value; });
      });
      setForm(nextForm);
      // 기준선은 form과 참조를 공유하면 안 되므로 깊은 복사로 스냅샷한다.
      setBaseline(JSON.parse(JSON.stringify(nextForm)));
      const p = d.permissions || {};
      const next: Record<string, string[]> = {};
      Object.keys(CAPABILITY_LABELS).forEach(k => {
        const v = p[k]?.value;
        next[k] = Array.isArray(v) ? v : [];
      });
      setPermissions(next);
    });
  }, []);

  const setW = (k) => (v) => setWeights({ ...weights, [k]: Math.max(0, Math.min(100, +v || 0)) });

  const togglePermission = (capability: string, role: string) => {
    setPermissions(prev => {
      const current = new Set(prev[capability] || []);
      if (current.has(role)) current.delete(role); else current.add(role);
      return { ...prev, [capability]: Array.from(current) };
    });
  };

  const patchSettings = (sectionKey, values) =>
    fetch("/api/v1/settings", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ section: sectionKey, values }),
    });

  const saveMatching = async () => {
    const policy = {};
    MATCHING_POLICY_KEYS.forEach(k => { policy[k] = fval("matching", k); });
    await patchSettings("matching", {
      weight_distance:      weights.distance,
      weight_qualification: weights.qualification,
      weight_availability:  weights.availability,
      weight_load:          weights.load,
      weight_history:       weights.history,
      weight_rating:        weights.rating,
      ...policy,
    });
  };

  const savePermissions = async () => {
    await patchSettings("permissions", permissions);
    // Refresh sidebar nav state — capabilities may have changed.
    window.dispatchEvent(new Event("session:refresh"));
  };

  // 매칭 외 섹션의 일반 저장 — SECTION_KEYS에 등록된 편집 가능 키만 모아 보낸다.
  const saveSection = async (sec) => {
    const keys = SECTION_KEYS[sec] || [];
    const values = {};
    keys.forEach(k => { values[k] = fval(sec, k); });
    await patchSettings(sec, values);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (section === "permissions")    await savePermissions();
      else if (section === "matching")  await saveMatching();
      else                              await saveSection(section);
      // 저장 성공 → 활성 섹션 기준선을 현재 값으로 갱신(이제 '변경 없음').
      setBaseline(b => ({ ...b, [section]: { ...(form[section] || {}) } }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } finally {
      setSaving(false);
    }
  };

  // SECTION_KEYS 기반 섹션(general/notification/data/security/integration)의 '저장되지
  // 않은 변경' 여부. matching/permissions는 weights/permissions 별도 state라 여기선 제외
  // (그 탭의 저장 버튼은 항상 활성 유지 — 아래 dirtyTracked 가드).
  const dirtyTracked = !!SECTION_KEYS[section];
  const isSectionDirty = (sec) => {
    const keys = SECTION_KEYS[sec];
    if (!keys || !baseline[sec]) return false;
    return keys.some(k => (form[sec]?.[k] ?? null) !== (baseline[sec]?.[k] ?? null));
  };
  const dirty = isSectionDirty(section);

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="platform" active="settings"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar title="시스템 설정" sub="플랫폼 전역 설정"/>
          <div className="main">

            <div style={{display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:18}}>
              <div>
                <div className="h1">시스템 설정</div>
                <div className="muted" style={{marginTop:4, fontSize:13}}>설정 변경은 감사 로그에 기록됩니다. 운영자에게는 5분 내 동기화됩니다.</div>
              </div>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                {dirty && !saved && (
                  <span style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:12, fontWeight:700,
                    color:'var(--orange-700, #9a4a1f)', background:'var(--orange-50, #fdf1e7)',
                    border:'1px solid var(--orange-200, #f3c9a8)', borderRadius:99, padding:'5px 10px'}}>
                    <span style={{width:7, height:7, borderRadius:'50%', background:'var(--orange-500)'}}/>
                    저장되지 않은 변경
                  </span>
                )}
                {saved && <span className="badge badge-green" style={{padding:'6px 10px'}}>✓ 저장되었습니다</span>}
                <a className="btn btn-ghost btn-md" href="/audit?q=setting_change" style={{textDecoration:'none'}}>변경 이력</a>
                <button className="btn btn-primary btn-md" onClick={save}
                  disabled={saving || (dirtyTracked && !dirty)}
                  title={dirtyTracked && !dirty ? "저장할 변경 사항이 없습니다" : "변경 사항을 저장합니다"}
                  style={dirty ? { boxShadow: '0 0 0 3px var(--orange-100, #fbe3cf)' } : undefined}>
                  {saving ? "저장 중…" : "저장"}
                </button>
              </div>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'200px 1fr', gap:14}}>

              <div className="card" style={{padding:8, alignSelf:'start'}}>
                {SECTIONS.map(s => (
                  <button key={s.k} onClick={()=>setSection(s.k)} className="nav-item"
                    style={{
                      cursor:'pointer', border:'none', width:'100%', justifyContent:'flex-start',
                      background: section === s.k ? 'var(--orange-50)' : 'transparent',
                      color: section === s.k ? 'var(--orange-700)' : 'var(--ink-2)',
                      fontWeight: section === s.k ? 700 : 500,
                    }}>
                    <span className="ico" style={{color: section === s.k ? 'var(--orange-600)' : 'var(--ink-3)'}}>{Ic[s.icon]}</span>
                    <span>{s.label}</span>
                    {/* 연동 탭은 백엔드 소비처가 없는 stored-only 목업이라 '준비중'으로 표시. */}
                    {s.k === 'integration' && (
                      <span style={{
                        marginLeft:'auto', fontSize:10, fontWeight:700, color:'var(--ink-3)',
                        background:'var(--bg-sub)', border:'1px solid var(--border)',
                        borderRadius:99, padding:'1px 7px', letterSpacing:'0.02em',
                      }}>준비중</span>
                    )}
                  </button>
                ))}
              </div>

              <div>
                {section === 'general' && (
                  <div className="card" style={{padding:22}}>
                    <div className="h3">일반</div>
                    <div className="muted" style={{fontSize:12, marginTop:2}}>플랫폼 기본 정보</div>
                    <Row label="플랫폼 이름"><input className="input" value={fval('general','site_name')} onChange={e=>fset('general','site_name')(e.target.value)}/></Row>
                    <Row label="기본 시간대"><select className="select" value={fval('general','default_timezone','Asia/Seoul')} onChange={e=>fset('general','default_timezone')(e.target.value)}><option>Asia/Seoul</option><option>UTC</option></select></Row>
                    <Row label="기본 언어"><select className="select" value={fval('general','default_locale','ko')} onChange={e=>fset('general','default_locale')(e.target.value)}><option value="ko">한국어</option><option value="en">English</option></select></Row>
                    <Row label="고객지원 번호"><input className="input" value={fval('general','support_phone')} onChange={e=>fset('general','support_phone')(e.target.value)}/></Row>
                    <Row label="긴급 신고 회선"><input className="input" value={fval('general','emergency_line')} onChange={e=>fset('general','emergency_line')(e.target.value)}/></Row>
                    <Row label="유지보수 모드"><Toggle on={!!fval('general','maintenance_mode',false)} onChange={fset('general','maintenance_mode')}/></Row>
                  </div>
                )}

                {section === 'permissions' && (
                  <div className="card" style={{padding:22}}>
                    <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:14}}>
                      <div>
                        <div className="h3">역할별 권한</div>
                        <div className="muted" style={{fontSize:12, marginTop:2}}>
                          기능(행)을 어떤 역할(열)이 수행할 수 있는지 정합니다. 사이드바와 백엔드 가드가 같은 표를 읽으므로,
                          체크를 끄면 해당 역할은 메뉴에서 항목이 사라지고 API 호출도 거부됩니다.
                        </div>
                      </div>
                    </div>
                    <div style={{marginTop:12, fontSize:11, color:'var(--ink-3)'}}>
                      ※ 시스템 관리자는 항상 모든 권한을 가집니다 (잠금 방지). 변경 사항은 감사 로그(<code>setting_change</code>)에 기록됩니다.
                    </div>
                    <div style={{overflowX:'auto', marginTop:14}}>
                      <table className="tbl" style={{minWidth:880}}>
                        <thead><tr>
                          <th style={{paddingLeft:14, textAlign:'left', position:'sticky', left:0, background:'var(--surface-1)'}}>기능</th>
                          {PERMISSION_ROLES.map(r => (
                            <th key={r.k} style={{textAlign:'center', whiteSpace:'nowrap', padding:'0 6px'}}>{r.label}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {Object.entries(CAPABILITY_LABELS).map(([cap, meta]) => {
                            const locked = LOCKED_CAPABILITIES.has(cap);
                            return (
                            <tr key={cap}>
                              <td style={{paddingLeft:14, position:'sticky', left:0, background:'var(--surface-1)'}}>
                                <div style={{fontWeight:700, fontSize:12.5, display:'flex', alignItems:'center', gap:6}}>
                                  {meta.label}
                                  {locked && <span title="시스템 관리자 전용으로 고정된 권한입니다 (변경 불가)"
                                    style={{fontSize:10}}>🔒</span>}
                                </div>
                                <div className="muted" style={{fontSize:11}}>
                                  {meta.desc}{locked && " · 시스템 관리자 전용 고정"}
                                </div>
                              </td>
                              {PERMISSION_ROLES.map(r => {
                                const isSysAdmin = r.k === 'system_admin';
                                // 잠긴 행은 system_admin만 허용으로 고정 표시(백엔드 enforce와 일치).
                                const checked = isSysAdmin || (!locked && (permissions[cap] || []).includes(r.k));
                                const disabled = isSysAdmin || locked;
                                return (
                                  <td key={r.k} style={{textAlign:'center'}}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      disabled={disabled}
                                      onChange={() => { if (!disabled) togglePermission(cap, r.k); }}
                                      title={
                                        isSysAdmin ? "시스템 관리자는 항상 허용됩니다"
                                        : locked   ? "이 권한은 시스템 관리자 전용으로 고정되어 변경할 수 없습니다"
                                        : `${r.label} → ${meta.label}`
                                      }
                                      style={{cursor: disabled ? 'not-allowed' : 'pointer', accentColor:'var(--orange-500)'}}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          );})}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {section === 'matching' && (
                  <>
                    <div className="card" style={{padding:22}}>
                      <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between'}}>
                        <div>
                          <div className="h3">AI 매칭 가중치</div>
                          <div className="muted" style={{fontSize:12, marginTop:2}}>6개 차원의 합이 100%가 되어야 합니다. 현재 합계: <b style={{color: total === 100 ? 'var(--green-600)' : 'var(--rose-500)'}}>{total}%</b></div>
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={resetWeights}
                          title="25/20/15/15/15/10 기본값으로 복귀">기본값으로 초기화</button>
                      </div>
                      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginTop:18}}>
                        {[
                          { k:"distance",      l:"거리·이동시간", d:"매니저 ↔ 어르신 직선거리·실시간 동선" },
                          { k:"qualification", l:"자격·전문성",   d:"사회복지사·간호조무사 등 보유 자격 적합도" },
                          { k:"availability",  l:"가용시간",      d:"이번 주 근무시간 여유(headroom)" },
                          { k:"load",          l:"매니저 부하",   d:"현재 배정된 케이스 부하" },
                          { k:"history",       l:"과거 관계",     d:"이 어르신과의 이전 방문·케이스 이력" },
                          { k:"rating",        l:"평가 점수",     d:"보호자/케이스 평가 평균" },
                        ].map(f => (
                          <div key={f.k} className="card card-pad" style={{padding:14}}>
                            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                              <div style={{fontWeight:700, fontSize:13}}>{f.l}</div>
                              <div className="mono" style={{fontWeight:800, color:'var(--orange-600)'}}>{weights[f.k]}%</div>
                            </div>
                            <div className="muted" style={{fontSize:11, marginTop:2}}>{f.d}</div>
                            <input type="range" min="0" max="100" value={weights[f.k]} onChange={e=>setW(f.k)(e.target.value)} style={{width:'100%', marginTop:10, accentColor:'var(--orange-500)'}}/>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="card" style={{padding:22, marginTop:14}}>
                      <div className="h3">매칭 정책</div>
                      <Row label="자동 배정 임계 점수"><input className="input" type="number" value={fval('matching','auto_assign_threshold',85)} onChange={e=>fset('matching','auto_assign_threshold')(+e.target.value)}/><span className="muted" style={{fontSize:11, marginLeft:8}}>점 이상이면 자동 배정</span></Row>
                      <Row label="후보 매니저 최소 수"><input className="input" type="number" style={{maxWidth:120}} value={fval('matching','min_candidates',3)} onChange={e=>fset('matching','min_candidates')(+e.target.value)}/></Row>
                      <Row label="응답 대기 시간"><input className="input" type="number" style={{maxWidth:120}} value={fval('matching','response_wait_min',15)} onChange={e=>fset('matching','response_wait_min')(+e.target.value)}/><span className="muted" style={{fontSize:11, marginLeft:8}}>분 (이후 차순위 후보 호출)</span></Row>
                      <Row label="동일 매니저 연속 배정 제한"><Toggle on={!!fval('matching','block_repeat_manager',true)} onChange={fset('matching','block_repeat_manager')}/><span className="muted" style={{fontSize:11, marginLeft:10}}>같은 어르신에게 3주 연속 동일 매니저 자동 배정 차단</span></Row>
                    </div>
                  </>
                )}

                {section === 'notification' && (
                  <div className="card" style={{padding:22}}>
                    <div style={{display:'flex', alignItems:'center'}}><div className="h3">알림 정책</div><SoonBadge/></div>
                    <div className="muted" style={{fontSize:12, marginTop:2}}>아래 역할별 라우팅 매트릭스는 향후 적용될 정책 예시입니다. 현재 외부 발송은 이메일 채널이 동작하며, 푸시·SMS는 준비 중입니다.</div>
                    {dirty && (
                      <div style={{marginTop:10, display:'flex', alignItems:'center', gap:8, fontSize:12, fontWeight:600,
                        color:'var(--orange-700, #9a4a1f)', background:'var(--orange-50, #fdf1e7)',
                        border:'1px solid var(--orange-200, #f3c9a8)', borderRadius:8, padding:'8px 12px'}}>
                        <span style={{width:7, height:7, borderRadius:'50%', background:'var(--orange-500)', flex:'none'}}/>
                        변경한 토글은 아직 적용되지 않았습니다 — 오른쪽 위 ‘저장’을 눌러야 반영됩니다.
                      </div>
                    )}
                    <table className="tbl" style={{marginTop:14, opacity:0.6}}>
                      <thead><tr>
                        <th style={{paddingLeft:14}}>이벤트</th>
                        <th>케어매니저</th>
                        <th>권역 관리자</th>
                        <th>보호자</th>
                        <th>유관기관</th>
                      </tr></thead>
                      <tbody>
                        {[
                          ["케어 신청 접수",       "—", "푸시·메일", "이메일", "—"],
                          ["매칭 제안",          "푸시·SMS", "푸시", "—", "—"],
                          ["방문 시작/종료",      "푸시", "—", "푸시", "—"],
                          ["이상 신호 (위험)",     "푸시·전화", "푸시·SMS", "푸시·SMS", "—"],
                          ["승인 대기",          "—", "푸시·메일", "—", "메일"],
                          ["주간 리포트",         "—", "메일 (월요일 08:00)", "—", "메일"],
                          ["보안: 비정상 로그인",  "—", "메일·SMS", "—", "—"],
                        ].map((row, i) => (
                          <tr key={i}>
                            <td style={{paddingLeft:14, fontWeight:600}}>{row[0]}</td>
                            <td className="muted">{row[1]}</td>
                            <td className="muted">{row[2]}</td>
                            <td className="muted">{row[3]}</td>
                            <td className="muted">{row[4]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{marginTop:18, fontSize:12, fontWeight:700, color:'var(--ink-3)', letterSpacing:'0.04em'}}>발송 채널</div>
                    <Row label="푸시 알림"><Toggle on={!!fval('notification','push_enabled',true)} disabled/><SoonBadge/></Row>
                    <Row label="SMS 알림"><Toggle on={!!fval('notification','sms_enabled',false)} disabled/><SoonBadge/></Row>
                    <Row label="이메일 알림"><Toggle on={!!fval('notification','email_enabled',false)} onChange={fset('notification','email_enabled')}/><span className="muted" style={{fontSize:11, marginLeft:10}}>알림 발생 시 수신자 이메일로도 발송합니다 (사용자별 이메일 채널 끄기·미등록 주소는 자동 제외)</span></Row>
                    <Row label="야간 SMS 발송 제한"><Toggle on={!!fval('notification','quiet_hours_enabled',true)} onChange={fset('notification','quiet_hours_enabled')}/><span className="muted" style={{fontSize:11, marginLeft:10}}>야간(22:00–07:00)에는 SMS 발송을 차단합니다 (위험·SOS 알림 제외, 인앱 알림은 영향 없음)</span></Row>
                  </div>
                )}

                {section === 'security' && (
                  <div className="card" style={{padding:22}}>
                    <div className="h3">보안</div>
                    <Row label="세션 유지 시간"><input className="input" type="number" style={{maxWidth:120}} value={fval('security','session_timeout_hours',12)} onChange={e=>fset('security','session_timeout_hours')(+e.target.value)}/><span className="muted" style={{fontSize:11, marginLeft:8}}>시간 · 마지막 활동 후 이 시간 동안 요청이 없으면 자동 로그아웃 (0이면 비활성)</span></Row>
                    <Row label="비밀번호 최소 길이"><input className="input" type="number" style={{maxWidth:120}} value={fval('security','password_min_length',10)} onChange={e=>fset('security','password_min_length')(+e.target.value)}/></Row>
                    <Row label="비밀번호 만료"><input className="input" type="number" style={{maxWidth:120}} value={fval('security','password_max_age_days',90)} onChange={e=>fset('security','password_max_age_days')(+e.target.value)}/><span className="muted" style={{fontSize:11, marginLeft:8}}>일 · 만료 시 다음 로그인에서 비밀번호 변경을 강제합니다</span></Row>
                    <Row label="로그인 실패 잠금 임계"><input className="input" type="number" style={{maxWidth:120}} value={fval('security','lockout_max_attempts',5)} onChange={e=>fset('security','lockout_max_attempts')(+e.target.value)}/><span className="muted" style={{fontSize:11, marginLeft:8}}>회 실패 시 자동 잠금</span></Row>
                  </div>
                )}

                {section === 'data' && (
                  <div className="card" style={{padding:22}}>
                    <div className="h3">데이터 · 보존</div>
                    <Row label="감사 로그 보존(일반)"><input className="input" type="number" style={{maxWidth:120}} value={fval('data','retention_audit_short_days',90)} onChange={e=>fset('data','retention_audit_short_days')(+e.target.value)}/><span className="muted" style={{fontSize:11, marginLeft:8}}>일</span></Row>
                    <Row label="감사 로그 보존(권한·승인)"><input className="input" type="number" style={{maxWidth:120}} value={fval('data','retention_audit_long_days',1825)} onChange={e=>fset('data','retention_audit_long_days')(+e.target.value)}/><span className="muted" style={{fontSize:11, marginLeft:8}}>일</span></Row>
                    <Row label="방문 기록 보존"><input className="input" type="number" style={{maxWidth:120}} value={fval('data','retention_visit_record_years',10)} onChange={e=>fset('data','retention_visit_record_years')(+e.target.value)}/><span className="muted" style={{fontSize:11, marginLeft:8}}>년 · 개인정보보호법 의료/돌봄 기록 기준</span></Row>
                    <Row label="익명 통계 추출"><Toggle on={!!fval('data','anonymized_stats_enabled',true)} onChange={fset('data','anonymized_stats_enabled')}/><span className="muted" style={{fontSize:11, marginLeft:10}}>식별 정보 제거 후 정책연구 목적 사용</span></Row>
                    <Row label="삭제 요청 처리 시한"><input className="input" type="number" style={{maxWidth:120}} value={fval('data','deletion_sla_days',30)} onChange={e=>fset('data','deletion_sla_days')(+e.target.value)}/><span className="muted" style={{fontSize:11, marginLeft:8}}>일</span></Row>
                    <Row label="자동 백업"><span><b>매일 03:00</b> · 권역별 분리</span></Row>
                    <Row label="GDPR/PIPA 데이터 내보내기"><button className="btn btn-ghost btn-sm" disabled title="추후 지원합니다.">사용자 데이터 다운로드 양식</button></Row>
                  </div>
                )}

                {section === 'integration' && (
                  <div className="card" style={{padding:22}}>
                    <div className="h3">외부 연동</div>
                    <div className="muted" style={{fontSize:12, marginTop:2}}>현재 활성화된 연동 모듈</div>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12, marginTop:14}}>
                      {[
                        { key:"nhis_enabled",                     name:"NHIS 자격 확인 API",  desc:"건강보험·장기요양 자격 조회" },
                        { key:"kakao_alimtalk_enabled",           name:"카카오 알림톡",       desc:"보호자 푸시·SMS 대체" },
                        { key:"slack_ops_enabled",                name:"Slack 운영 알림",     desc:"내부 운영 채널" },
                        { key:"public_data_demographics_enabled", name:"공공데이터 인구통계",  desc:"권역별 인구 통계 동기화" },
                        { key:"nts_business_verify_enabled",      name:"국세청 사업자 검증",   desc:"유관기관 자격 검증" },
                      ].map((it, i) => (
                        <div key={i} className="card card-pad" style={{padding:14, display:'flex', alignItems:'center', gap:12}}>
                          <span style={{width:36, height:36, borderRadius:10, background:'var(--bg-sub)', display:'grid', placeItems:'center', color:'var(--orange-600)'}}>
                            <span style={{width:18,height:18,display:'inline-block'}}>{Ic.org}</span>
                          </span>
                          <div style={{flex:1, minWidth:0}}>
                            <div style={{fontWeight:700, fontSize:13}}>{it.name}</div>
                            <div className="muted" style={{fontSize:11}}>{it.desc}</div>
                          </div>
                          <Toggle on={!!fval('integration',it.key,false)} onChange={fset('integration',it.key)}/>
                        </div>
                      ))}
                    </div>
                    <Row label="신규 연동 요청"><button className="btn btn-ghost btn-md" disabled title="추후 지원합니다.">{Ic.plus} 연동 추가</button></Row>
                  </div>
                )}
              </div>

            </div>

          </div>
          <AppFooter/>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{display:'flex', alignItems:'center', gap:14, padding:'12px 0', borderTop:'1px solid var(--border-soft)'}}>
      <div style={{width:200, fontSize:12.5, fontWeight:600, color:'var(--ink-2)'}}>{label}</div>
      <div style={{flex:1, display:'flex', alignItems:'center'}}>{children}</div>
    </div>
  );
}

// controlled(on/onChange) 또는 uncontrolled(defaultOn) 양쪽 지원.
function Toggle({ defaultOn = false, on: onProp, onChange, disabled = false }) {
  const controlled = onProp !== undefined;
  const [internal, setInternal] = React.useState(defaultOn);
  const on = controlled ? onProp : internal;
  const toggle = () => { if (disabled) return; if (controlled) onChange(!on); else setInternal(!internal); };
  return (
    <button onClick={toggle} disabled={disabled} style={{
      width:42, height:24, borderRadius:99, border:'none',
      background: on ? 'var(--orange-500)' : 'var(--border)',
      position:'relative', cursor: disabled ? 'not-allowed' : 'pointer', transition:'background 0.15s',
      opacity: disabled ? 0.45 : 1,
    }}>
      <span style={{
        position:'absolute', top:3, left: on ? 21 : 3, width:18, height:18,
        borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.15)',
        transition:'left 0.15s',
      }}/>
    </button>
  );
}

// "준비 중" 배지 — 아직 백엔드 발송 경로가 없는(stored-only) 항목 표시용.
const SoonBadge = () => (
  <span style={{
    marginLeft:10, fontSize:10.5, fontWeight:700, color:'var(--ink-3)',
    background:'var(--bg-sub)', border:'1px solid var(--border)',
    borderRadius:99, padding:'1px 8px', letterSpacing:'0.02em',
  }}>준비 중</span>
);

export { Settings };
